import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { ChevronLeft, ChevronRight, Plus, CalendarDays, List, Car } from "lucide-react";
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  addDays, format, isSameMonth, isToday, addMonths, parseISO, isWithinInterval, subDays, differenceInDays,
} from "date-fns";

// Format "20:00" -> "8pm", "09:00" -> "9am"
const formatShortTime = (t) => {
  if (!t) return null;
  const [hStr, mStr = "0"] = t.split(":");
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10) || 0;
  if (Number.isNaN(h)) return t;
  const ampm = h < 12 ? "am" : "pm";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${h12}${ampm}` : `${h12}:${String(m).padStart(2, "0")}${ampm}`;
};

export default function CalendarPage() {
  const [cursor, setCursor] = useState(new Date());
  const [bookings, setBookings] = useState([]);
  const [scope, setScope] = useState("all"); // all | mine
  const [view, setView] = useState("calendar"); // calendar | list
  const [timeFilter, setTimeFilter] = useState("upcoming"); // upcoming | past | all
  const [listPage, setListPage] = useState(0);
  const PAGE_SIZE = 20;

  useEffect(() => {
    api.get("/bookings").then((r) => setBookings(r.data)).catch(console.error);
  }, []);

  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 });
    const arr = [];
    let d = start;
    while (d <= end) { arr.push(d); d = addDays(d, 1); }
    return arr;
  }, [cursor]);

  const visible = scope === "mine" ? bookings.filter((b) => b.is_my_stay) : bookings;

  // Returns [{ booking, role }] for the given day
  const dayBookings = (day) => {
    const iso = format(day, "yyyy-MM-dd");
    const out = [];
    for (const b of visible) {
      if (b.status !== "confirmed") continue;
      if (iso === b.arrival_date && iso === b.departure_date) {
        out.push({ b, role: "mid" });
      } else if (iso === b.arrival_date) {
        out.push({ b, role: "arrive" });
      } else if (iso === b.departure_date) {
        out.push({ b, role: "depart" });
      } else if (
        isWithinInterval(parseISO(iso), { start: parseISO(b.arrival_date), end: subDays(parseISO(b.departure_date), 1) })
      ) {
        out.push({ b, role: "mid" });
      }
    }
    return out;
  };

  const conflictIds = new Set();
  for (const b of visible.filter((x) => x.status === "confirmed")) {
    if (b.conflicts?.length) {
      conflictIds.add(b.booking_id);
      b.conflicts.forEach((c) => conflictIds.add(c));
    }
  }

  // List view data
  const today = new Date().toISOString().slice(0, 10);
  const listItems = useMemo(() => {
    const base = visible
      .filter((b) => b.status === "confirmed")
      .filter((b) => {
        if (timeFilter === "all") return true;
        if (timeFilter === "past") return b.departure_date < today;
        return b.departure_date >= today; // upcoming includes in-progress
      });
    if (timeFilter === "past") {
      // Most recent first
      return base.sort((a, b) => b.arrival_date.localeCompare(a.arrival_date));
    }
    return base.sort((a, b) => a.arrival_date.localeCompare(b.arrival_date));
  }, [visible, timeFilter, today]);

  // Reset pagination when filters change
  useEffect(() => { setListPage(0); }, [timeFilter, scope]);
  const listPaged = listItems.slice(listPage * PAGE_SIZE, (listPage + 1) * PAGE_SIZE);
  const listTotalPages = Math.max(1, Math.ceil(listItems.length / PAGE_SIZE));

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.25em] text-[#7A7A7A]">Estancias</p>
          <h1 className="font-serif-villa text-4xl sm:text-5xl">Stays at the casa</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Scope filter */}
          <FilterBtn active={scope === "all"} onClick={() => setScope("all")} testid="filter-all">All</FilterBtn>
          <FilterBtn active={scope === "mine"} onClick={() => setScope("mine")} testid="filter-mine">My stays</FilterBtn>
          {/* View toggle */}
          <div className="ml-2 inline-flex rounded-full border border-[#E8E1D5] overflow-hidden bg-[#FDFBF7]" data-testid="view-toggle">
            <button
              onClick={() => setView("calendar")}
              data-testid="view-calendar"
              className={`px-3 py-2 text-sm inline-flex items-center gap-1 transition-colors ${view === "calendar" ? "bg-[#2B2B2B] text-[#FDFBF7]" : "hover:bg-[#F5F0E6]"}`}
            >
              <CalendarDays className="w-4 h-4" /> Calendar
            </button>
            <button
              onClick={() => setView("list")}
              data-testid="view-list"
              className={`px-3 py-2 text-sm inline-flex items-center gap-1 transition-colors ${view === "list" ? "bg-[#2B2B2B] text-[#FDFBF7]" : "hover:bg-[#F5F0E6]"}`}
            >
              <List className="w-4 h-4" /> List
            </button>
          </div>
          <Link to="/bookings/new" className="ml-2 inline-flex items-center gap-2 villa-btn-primary px-4 py-2 rounded-full text-sm" data-testid="cal-new-booking">
            <Plus className="w-4 h-4" /> New
          </Link>
        </div>
      </div>

      {view === "calendar" ? (
        <div className="villa-card p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => setCursor(addMonths(cursor, -1))}
              data-testid="cal-prev-month"
              className="p-2 rounded-full hover:bg-[#EAE4D8] text-[#2B2B2B]"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="text-center">
              <div className="font-serif-villa text-2xl">{format(cursor, "MMMM yyyy")}</div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-[#7A7A7A] mt-1">
                {format(cursor, "MMMM yyyy")}
              </div>
            </div>
            <button
              onClick={() => setCursor(addMonths(cursor, 1))}
              data-testid="cal-next-month"
              className="p-2 rounded-full hover:bg-[#EAE4D8] text-[#2B2B2B]"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-7 text-[11px] uppercase tracking-[0.2em] text-[#9A9A9A] mb-2">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
              <div key={d} className="px-2 py-1">{d}</div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1 sm:gap-2">
            {days.map((d) => {
              const list = dayBookings(d);
              const muted = !isSameMonth(d, cursor);
              const today = isToday(d);
              const occupiedCount = list.filter((x) => x.role !== "depart").length;
              return (
                <div
                  key={d.toISOString()}
                  className={`cal-day ${muted ? "muted" : ""} ${today ? "today" : ""}`}
                  data-testid={`cal-day-${format(d, "yyyy-MM-dd")}`}
                >
                  <div className="text-xs text-[#2B2B2B] flex items-center justify-between">
                    <span>{format(d, "d")}</span>
                    {occupiedCount > 1 && <span className="text-[10px] text-[#D9534F]">!</span>}
                  </div>
                  <div className="mt-1 space-y-0.5 overflow-hidden">
                    {list.slice(0, 3).map(({ b, role }) => {
                      const guestSuffix = b.primary_guest_name ? ` (${b.primary_guest_name})` : "";
                      const baseTitle = `${b.title || "Stay"}${guestSuffix}`;
                      let prefix = "";
                      if (role === "arrive" && b.arrival_time) prefix = `Arrive ${formatShortTime(b.arrival_time)} · `;
                      else if (role === "depart" && b.departure_time) prefix = `Depart ${formatShortTime(b.departure_time)} · `;
                      else if (role === "depart") prefix = "Depart · ";
                      else if (role === "arrive") prefix = "Arrive · ";
                      const isPending = b.approval_status === "pending";
                      return (
                        <Link
                          key={b.booking_id + role}
                          to={`/bookings/${b.booking_id}`}
                          className={`pill block ${b.cost_category === "friends" ? "friends" : ""} ${conflictIds.has(b.booking_id) && occupiedCount > 1 ? "conflict" : ""} ${isPending ? "opacity-70" : ""}`}
                          title={`${prefix}${baseTitle}${isPending ? " — awaiting approval" : ""}`}
                          data-testid={`cal-pill-${b.booking_id}-${role}`}
                        >
                          {prefix}{baseTitle}{isPending ? " ·⏳" : ""}
                        </Link>
                      );
                    })}
                    {list.length > 3 && (
                      <div className="text-[10px] text-[#7A7A7A]">+{list.length - 3} more</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="villa-card p-4 sm:p-6" data-testid="list-view">
          {/* Past / Upcoming filter */}
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <div className="flex flex-wrap items-center gap-2" data-testid="list-time-filter">
              <FilterBtn active={timeFilter === "upcoming"} onClick={() => setTimeFilter("upcoming")} testid="filter-upcoming">Upcoming</FilterBtn>
              <FilterBtn active={timeFilter === "past"} onClick={() => setTimeFilter("past")} testid="filter-past">Past</FilterBtn>
              <FilterBtn active={timeFilter === "all"} onClick={() => setTimeFilter("all")} testid="filter-time-all">All</FilterBtn>
            </div>
            <span className="text-xs text-[#9A9A9A]">{listItems.length} {listItems.length === 1 ? "stay" : "stays"}</span>
          </div>
          {listItems.length === 0 ? (
            <p className="text-sm text-[#9A9A9A] italic py-6 text-center">
              {scope === "mine" ? "Nothing of yours booked yet." : "No stays yet — la casa está tranquila."}
            </p>
          ) : (
            <>
            <ul className="divide-y divide-[#E8E1D5]">
              {listPaged.map((b) => {
                const isPast = b.departure_date < today;
                const nights = differenceInDays(parseISO(b.departure_date), parseISO(b.arrival_date));
                const isPending = b.approval_status === "pending";
                return (
                  <li key={b.booking_id} className={`py-4 flex flex-wrap items-center justify-between gap-3 ${isPast ? "opacity-50" : ""}`}>
                    <div>
                      <Link
                        to={`/bookings/${b.booking_id}`}
                        data-testid={`list-row-${b.booking_id}`}
                        className="font-serif-villa text-lg hover:text-[#C25E3E]"
                      >
                        {b.title || "Family stay"}{b.primary_guest_name ? ` (${b.primary_guest_name})` : ""}
                      </Link>
                      <div className="text-sm text-[#7A7A7A] mt-0.5">
                        {format(parseISO(b.arrival_date), "EEE d MMM yyyy")}
                        {b.arrival_time && <> · arrive {formatShortTime(b.arrival_time)}</>}
                        <span className="mx-2">→</span>
                        {format(parseISO(b.departure_date), "EEE d MMM yyyy")}
                        {b.departure_time && <> · depart {formatShortTime(b.departure_time)}</>}
                        <span className="mx-2">·</span>{nights} {nights === 1 ? "night" : "nights"}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {isPending && <span className="villa-chip villa-chip-warn">Awaiting approval</span>}
                      {b.car_required && <span className="villa-chip"><Car className="w-3 h-3 inline mr-1" /> Car</span>}
                      <span className={`villa-chip ${b.cost_category === "friends" ? "" : "villa-chip-ok"}`}>{b.cost_category}</span>
                      {b.conflicts?.length > 0 && (
                        <span className="villa-chip villa-chip-warn">Conflict</span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
            {listTotalPages > 1 && (
              <div className="flex items-center justify-center gap-2 pt-4" data-testid="list-pagination">
                <button
                  type="button"
                  onClick={() => setListPage(Math.max(0, listPage - 1))}
                  disabled={listPage === 0}
                  data-testid="list-page-prev"
                  className="px-3 py-1.5 rounded-full text-xs bg-[#FDFBF7] border border-[#E8E1D5] text-[#2B2B2B] disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#F5F0E6]"
                >← Prev</button>
                <span className="text-xs text-[#7A7A7A]" data-testid="list-page-indicator">Page {listPage + 1} of {listTotalPages}</span>
                <button
                  type="button"
                  onClick={() => setListPage(Math.min(listTotalPages - 1, listPage + 1))}
                  disabled={listPage >= listTotalPages - 1}
                  data-testid="list-page-next"
                  className="px-3 py-1.5 rounded-full text-xs bg-[#FDFBF7] border border-[#E8E1D5] text-[#2B2B2B] disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#F5F0E6]"
                >Next →</button>
              </div>
            )}
            </>
          )}
        </div>
      )}

      {/* Legend - calendar only */}
      {view === "calendar" && (
        <div className="flex flex-wrap items-center gap-4 text-xs text-[#7A7A7A]">
          <span className="flex items-center gap-2"><span className="inline-block w-3 h-3 rounded bg-[rgba(194,94,62,0.2)]" /> Family stay</span>
          <span className="flex items-center gap-2"><span className="inline-block w-3 h-3 rounded bg-[rgba(69,123,157,0.2)]" /> Friends stay</span>
          <span className="flex items-center gap-2"><span className="inline-block w-3 h-3 rounded bg-[rgba(217,83,79,0.2)] outline outline-1 outline-dashed outline-[#D9534F]" /> Overlap / conflict</span>
        </div>
      )}
    </div>
  );
}

function FilterBtn({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-2 rounded-full text-sm transition-colors duration-300 ${
        active ? "bg-[#2B2B2B] text-[#FDFBF7]" : "bg-[#FDFBF7] border border-[#E8E1D5] text-[#2B2B2B] hover:bg-[#F5F0E6]"
      }`}
    >
      {children}
    </button>
  );
}
